import _ from 'lodash'
import {
	pointers,
	Structure,
	Data,
	QueryProperty,
	ControllerConnectionItem,
	ConnectionType,
	BaseDataItemWithMetadata,
	BaseDataItem,
	RepeaterDataItem,
	UnresolvedRefArray,
	PresetData,
	ResolvedVariantsRefArray,
	ResolvedVariantRelation,
	LayoutDataItems,
	Style,
	UnresolvedVariantRelation,
} from '@wix/thunderbolt-becky-types'
import { InflatedByRefComp, InflatedByRefStructure, RefData, ResolvedRef } from '../types/byRef'
import { DATA_QUERIES_TO_INFLATE, getEmptyDataMaps, getUniqueId, removeHash } from './utils'
import { shouldResolveRefArray } from './presetsUtils'
import { getUniqueDataQuery } from './byRefFunctionLibrary'

const DATA_MAPS = pointers.data.DATA_MAPS
const PROPERTY_TYPES = pointers.components.PROPERTY_TYPES
const QUERY_TO_MAP_NAME = pointers.QUERY_TO_MAP_NAME
const REPEATER_ITEM_DELIMETER = '__'

const getRepeaterAncestorIfExists = (id: string | null, structure: Structure): string | null => {
	if (!id || !structure[id]) {
		return null
	}

	if (structure[id].componentType === 'Repeater') {
		return id
	}

	return getRepeaterAncestorIfExists(structure[id].parent, structure)
}

const fixInflatedConnectionItem = (
	connectionItem: ControllerConnectionItem,
	originalStructure: Structure,
	refId: string
) => {
	if (connectionItem.type !== ConnectionType.ConnectionItem || !connectionItem.controllerId) {
		return connectionItem
	}

	const originalComp = Object.values(originalStructure).find(
		(value) => value.dataQuery === connectionItem.controllerId
	)

	if (!originalComp) {
		return connectionItem
	}

	const oldCompUniqueId = getUniqueId(refId, originalComp.id)
	const inflatedId = getUniqueDataQuery(oldCompUniqueId, PROPERTY_TYPES.DATA_QUERY)

	return { ...connectionItem, controllerId: inflatedId }
}

const inflateSingleDataItem = (
	originalDataItem: BaseDataItem<string>,
	dataQueryName: QueryProperty,
	comp: InflatedByRefComp,
	resolvedStructure: Structure,
	refId: string
) => {
	const inflatedDataQuery = comp[dataQueryName]
	const inflatedDataItem = { ...originalDataItem, id: inflatedDataQuery }
	if (dataQueryName === 'connectionQuery' && inflatedDataItem.type === 'ConnectionList') {
		// @ts-ignore
		inflatedDataItem.items = inflatedDataItem.items.map((connectionItem) =>
			fixInflatedConnectionItem(connectionItem, resolvedStructure, refId)
		)
	}

	return inflatedDataItem
}

const inflateRepeaterItems = (
	repeaterAncestorId: string,
	dataMapName: 'design_data' | 'document_data',
	dataQueryName: 'designQuery' | 'dataQuery',
	comp: InflatedByRefComp,
	originalInflatedDataItemId: string,
	remoteAndMasterPageStructure: Structure | InflatedByRefStructure,
	masterPageData: Data | RefData,
	resolvedData: Data | RefData,
	pageCompId: string
): { [id: string]: BaseDataItemWithMetadata } => {
	const { dataQuery: repeaterDataQuery } = remoteAndMasterPageStructure[repeaterAncestorId]

	const repeaterDataItem =
		repeaterDataQuery &&
		((masterPageData[DATA_MAPS.DATA][repeaterDataQuery] ||
			resolvedData[DATA_MAPS.DATA][repeaterDataQuery]) as RepeaterDataItem)

	if (!repeaterDataItem) {
		return {}
	}

	const { items } = repeaterDataItem

	return items.reduce((acc: { [id: string]: BaseDataItemWithMetadata }, item: string) => {
		const originalDataItemId = `${
			remoteAndMasterPageStructure[comp.templateId][dataQueryName]
		}${REPEATER_ITEM_DELIMETER}${item}`
		const originalItemData = (masterPageData[dataMapName][originalDataItemId] ||
			resolvedData[dataMapName][originalDataItemId]) as BaseDataItemWithMetadata

		if (originalItemData) {
			const inflatedDataItemId = `${originalInflatedDataItemId}${REPEATER_ITEM_DELIMETER}${item}`
			acc[inflatedDataItemId] = {
				...originalItemData,
				id: inflatedDataItemId,
				metaData: { ...originalItemData.metaData, pageId: pageCompId },
			} as BaseDataItemWithMetadata
		}
		return acc
	}, {})
}

const getOriginalDataItem = (
	dataQueryName: typeof DATA_QUERIES_TO_INFLATE[number], // TODO TS
	comp: InflatedByRefComp,
	resolvedStructure: InflatedByRefStructure,
	resolvedData: Data | RefData
) => {
	const inflatedDataQuery = comp[dataQueryName]
	if (!inflatedDataQuery) {
		return
	}
	const originalCompId = comp.templateId
	const originalDataQuery = resolvedStructure[originalCompId][dataQueryName]
	if (!originalDataQuery) {
		return
	}
	const dataMapName = QUERY_TO_MAP_NAME[dataQueryName]
	return resolvedData[dataMapName][originalDataQuery]
}

const inflateDataForRepeater = ({
	comp,
	dataMapName,
	dataQueryName,
	inflatedDataItem,
	masterPageData,
	resolvedData,
	pageCompId,
	inflatedDataAcc,
	getRemoteAndMasterPageStructure,
}: {
	comp: InflatedByRefComp
	dataMapName: 'design_data' | 'document_data'
	dataQueryName: 'dataQuery' | 'designQuery'
	inflatedDataItem: BaseDataItem<string>
	masterPageData: Data | RefData
	resolvedData: Data | RefData
	pageCompId: string
	inflatedDataAcc: RefData
	getRemoteAndMasterPageStructure: () => Structure
}) => {
	const remoteAndMasterPageStructure = getRemoteAndMasterPageStructure()
	const repeaterAncestorId = getRepeaterAncestorIfExists(comp.templateId, remoteAndMasterPageStructure)
	if (!repeaterAncestorId) {
		return
	}
	_.assign(
		inflatedDataAcc[dataMapName],
		inflateRepeaterItems(
			repeaterAncestorId,
			dataMapName,
			dataQueryName,
			comp,
			inflatedDataItem.id,
			remoteAndMasterPageStructure,
			masterPageData,
			resolvedData,
			pageCompId
		)
	)
}

const inflateRefArray = ({
	resolvedData,
	inflatedDataAcc,
	mapName,
	refArray,
	dataQuery,
	presets,
}: {
	refArray: UnresolvedRefArray
	dataQuery: string
	mapName: 'layout_data' | 'theme_data'
	resolvedData: Data | RefData
	inflatedDataAcc: RefData
	presets: ResolvedVariantsRefArray<PresetData>['values']
}) => {
	const resolvedRefArray = {
		...refArray,
		values: refArray.values
			.map((value) => resolvedData[mapName][removeHash(value)])
			.map((value) =>
				value.type === 'VariantRelation'
					? { ...value, to: resolvedData[mapName][removeHash((value as UnresolvedVariantRelation).to)] }
					: value
			),
	}

	const presetItemKey = mapName === 'layout_data' ? 'layout' : 'style'

	const getItemUnderPresetCondition = (presetCondition: string) => {
		const getDefaultItem = () => resolvedRefArray.values.find((value) => value.type !== 'VariantRelation')! // there should always be a default value

		const variantRelationWithPresetCondition = resolvedRefArray.values.find(
			(value) =>
				value.type === 'VariantRelation' &&
				value.variants.some((variantId) => removeHash(variantId) === presetCondition)
		) as ResolvedVariantRelation<LayoutDataItems | Style>
		return variantRelationWithPresetCondition ? variantRelationWithPresetCondition.to : getDefaultItem()
	}

	// Replace VariantRelations that points to presets with new VariantRelations that points to consumer conditions
	const newRefArrayValues = presets.map((presetDataItem) => {
		if (presetDataItem.type === 'PresetData') {
			const item = getItemUnderPresetCondition(presetDataItem[presetItemKey])
			const itemId = `${dataQuery}-${item.id}`
			inflatedDataAcc[mapName][itemId] = {
				...item,
				id: itemId,
			} as Style | LayoutDataItems
			return itemId
		}

		const presetItem = presetDataItem.to
		const item = getItemUnderPresetCondition(presetItem[presetItemKey])
		const itemId = `${dataQuery}-${item.id}`
		inflatedDataAcc[mapName][itemId] = {
			...item,
			id: itemId,
		} as Style | LayoutDataItems

		const variantRelationId = `${dataQuery}-${presetDataItem.id}`

		inflatedDataAcc[mapName][variantRelationId] = {
			...presetDataItem,
			id: variantRelationId,
			to: `#${itemId}`,
		}

		return variantRelationId
	})

	// Replace RefArray with new new RefArray that points to the new VariantRelations
	inflatedDataAcc[mapName][dataQuery] = {
		...resolvedRefArray,
		id: dataQuery,
		values: newRefArrayValues.map((value) => `#${value}`),
	}
}

function inflateSingle({
	originalDataItem,
	refId,
	dataMapName,
	resolvedData,
	dataQueryName,
	getRemoteAndMasterPageStructure,
	inflatedDataAcc,
	pageCompId,
	masterPageData,
	comp,
	resolvedStructure,
}: {
	originalDataItem: BaseDataItem<string>
	dataQueryName: typeof DATA_QUERIES_TO_INFLATE[number] // TODO: TS
	comp: InflatedByRefComp
	resolvedStructure: InflatedByRefStructure
	refId: string
	inflatedDataAcc: RefData
	dataMapName: keyof Data
	masterPageData: Data | RefData
	resolvedData: Data | RefData
	pageCompId: string
	getRemoteAndMasterPageStructure: () => Structure
}) {
	const inflatedDataItem = inflateSingleDataItem(originalDataItem, dataQueryName, comp, resolvedStructure, refId)
	inflatedDataAcc[dataMapName][inflatedDataItem.id] = inflatedDataItem

	if (dataQueryName === 'dataQuery' || dataQueryName === 'designQuery') {
		inflateDataForRepeater({
			comp,
			dataMapName: dataMapName as 'design_data' | 'document_data',
			dataQueryName,
			inflatedDataItem,
			masterPageData,
			resolvedData,
			pageCompId,
			inflatedDataAcc,
			getRemoteAndMasterPageStructure,
		})
	}
}

export const inflateRefData = (
	inflatedStructure: InflatedByRefStructure,
	{ structure: resolvedStructure, data: resolvedData }: ResolvedRef,
	masterPageData: Data | RefData,
	refId: string,
	pageCompId: string,
	getRemoteAndMasterPageStructure: () => Structure,
	presets: ResolvedVariantsRefArray<PresetData>['values']
): RefData =>
	_.reduce(
		inflatedStructure,
		(inflatedDataAcc, comp) => {
			if (comp.componentType === 'RemoteRefDeadComp') {
				return inflatedDataAcc
			}
			DATA_QUERIES_TO_INFLATE.forEach((dataQueryName) => {
				const dataMapName = QUERY_TO_MAP_NAME[dataQueryName]
				const originalDataItem = getOriginalDataItem(dataQueryName, comp, resolvedStructure, resolvedData)
				if (originalDataItem) {
					if (shouldResolveRefArray(originalDataItem, resolvedData, dataMapName, presets)) {
						inflateRefArray({
							refArray: (originalDataItem as unknown) as UnresolvedRefArray,
							dataQuery: comp[dataQueryName],
							mapName: dataMapName as 'layout_data' | 'theme_data',
							resolvedData,
							inflatedDataAcc,
							presets,
						})
					} else {
						inflateSingle({
							originalDataItem,
							dataQueryName,
							comp,
							resolvedStructure,
							refId,
							inflatedDataAcc,
							dataMapName,
							masterPageData,
							resolvedData,
							pageCompId,
							getRemoteAndMasterPageStructure,
						})
					}
				}
			})
			return inflatedDataAcc
		},
		getEmptyDataMaps()
	)
